import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import ProviderBadges from '../ProviderBadges';
import { isFuelXDirect } from '../../data/uspProviders';

/**
 * Info-Icon mit Popover — via Portal auf document.body gerendert,
 * damit weder overflow-hidden noch transform-Container (3D-Tilt-Karten)
 * den Inhalt abschneiden können.
 * - Desktop: fixed Popover neben dem Icon (mit Viewport-Kollisionslogik).
 * - Mobile (< sm): Bottom-Sheet mit Backdrop.
 */
export default function USPInfoIcon({ text, title, badge, slug, painpoints, painpointsNote, dark = false, variant = 'icon', testId = 'usp-info' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches;

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    if (!isMobile && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = 380;
      const left = Math.max(8, Math.min(r.left - 8, window.innerWidth - width - 16));
      const spaceBelow = window.innerHeight - r.bottom;
      setPos(
        spaceBelow > 260
          ? { left, top: r.bottom + 8 }
          : { left, bottom: window.innerHeight - r.top + 8 }
      );
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    // Scrollen INNERHALB des Popovers darf es nicht schließen (Capture-Phase fängt alles)
    const onScroll = (e) => {
      if (isMobile) return;
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    if (isMobile) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
      document.body.style.overflow = '';
    };
  }, [open, isMobile]);

  return (
    <>
      {variant === 'pill' ? (
        <button
          ref={btnRef}
          type="button"
          onClick={toggle}
          className={`inline-flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.12em] transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
            dark
              ? 'bg-emerald-400 text-emerald-950 shadow-neon-sm hover:bg-emerald-300 hover:shadow-neon'
              : 'bg-white text-emerald-700 border border-emerald-200 shadow-sm hover:border-emerald-400 hover:shadow-neon-sm'
          }`}
          aria-label={`Mehr Infos zu ${title || 'diesem Vorteil'}`}
          data-testid={`${testId}-btn`}
        >
          <Info className="w-3 h-3" strokeWidth={2.4} /> Info
        </button>
      ) : (
        <button
          ref={btnRef}
          type="button"
          onClick={toggle}
          className={`inline-flex items-center justify-center w-4 h-4 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 transition-colors flex-shrink-0 align-middle ${
            dark ? 'text-emerald-200/70 hover:text-white' : 'text-stone-400 hover:text-emerald-600'
          }`}
          aria-label={`Mehr Infos zu ${title || 'diesem Vorteil'}`}
          data-testid={`${testId}-btn`}
        >
          <Info className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}

      {open && !isMobile && pos &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            className="fixed z-[120] w-[360px] max-h-[76vh] overflow-y-auto bg-white border border-stone-200 rounded-2xl shadow-xl p-3.5 text-left animate-in fade-in zoom-in-95 duration-150"
            style={{ left: pos.left, top: pos.top, bottom: pos.bottom }}
            data-testid={`${testId}-popover`}
          >
            {title && (
              <p className="text-[13px] font-semibold text-stone-900 mb-1 pr-6 leading-snug">{title}</p>
            )}
            {badge && (
              <span className="inline-block text-[9.5px] font-semibold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 mb-1.5">{badge}</span>
            )}
            <p className="text-[12px] text-stone-600 leading-relaxed">{text}</p>
            {painpoints?.length > 0 && (
              <div className="mt-2.5 rounded-xl bg-emerald-50/70 border border-emerald-100 p-2.5" data-testid={`${testId}-painpoints`}>
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-emerald-700 mb-1.5">Typische Sorgen — und was du bekommst</p>
                <ul className="space-y-1.5">
                  {painpoints.slice(0, 2).map((p) => (
                    <li key={p.pain}>
                      <p className="text-[11.5px] font-semibold text-stone-800 leading-snug">{p.pain}</p>
                      <p className="text-[11px] text-stone-500 leading-snug">{p.gain}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {slug && (
              <div className="mt-2.5 pt-2.5 border-t border-stone-100">
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-stone-400 mb-1.5">
                  {isFuelXDirect(slug) ? 'Direkt von FuelX — wir kümmern uns persönlich' : 'Dienstleister u.\u00a0a.'}
                </p>
                <ProviderBadges slug={slug} compact testId={`${testId}-pop-providers`} />
              </div>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="absolute top-2.5 right-2.5 text-stone-400 hover:text-stone-700 rounded-full p-1"
              aria-label="Schließen"
              data-testid={`${testId}-close`}
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>,
          document.body
        )}

      {open && isMobile &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[120] bg-stone-900/50 backdrop-blur-sm animate-in fade-in duration-200"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-backdrop`}
            />
            <div
              ref={popRef}
              role="dialog"
              className="fixed left-0 right-0 bottom-0 z-[121] bg-white rounded-t-3xl shadow-2xl border-t border-stone-200 p-5 pb-7 max-h-[75vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
              data-testid={`${testId}-popover`}
            >
              <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto mb-4" />
              {title && (
                <p className="text-[16px] font-semibold text-stone-900 mb-1.5 pr-8 leading-snug">{title}</p>
              )}
              {badge && (
                <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 mb-2">{badge}</span>
              )}
              <p className="text-[13.5px] text-stone-600 leading-relaxed">{text}</p>
              {painpoints?.length > 0 && (
                <div className="mt-3 rounded-xl bg-emerald-50/70 border border-emerald-100 p-3" data-testid={`${testId}-painpoints-m`}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 mb-2">Typische Sorgen — und was du bekommst</p>
                  <ul className="space-y-2">
                    {painpoints.slice(0, 2).map((p) => (
                      <li key={p.pain}>
                        <p className="text-[13px] font-semibold text-stone-800 leading-snug">{p.pain}</p>
                        <p className="text-[12.5px] text-stone-500 leading-snug">{p.gain}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {slug && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 mb-2">
                    {isFuelXDirect(slug) ? 'Direkt von FuelX — wir kümmern uns persönlich' : 'Dienstleister u.\u00a0a.'}
                  </p>
                  <ProviderBadges slug={slug} compact testId={`${testId}-pop-providers-m`} />
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 rounded-full p-1.5 bg-stone-100"
                aria-label="Schließen"
                data-testid={`${testId}-close`}
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
